import React, { useEffect, useState } from "react";
import FoundryBadge from "../components/foundry/FoundryBadge";
import FoundryButton from "../components/foundry/FoundryButton";
import FoundryCard from "../components/foundry/FoundryCard";
import WorkCenterTaskBoard from "./workCenterTaskBoardComponent";
import { WorkCenterMasterData } from "./useWorkCenterMasterData";
import { WorkCenterUser } from "./WorkCenterUsersSettings";
import { Initiative } from "./workCenterRoadmapData";
type Task = {
  id: string;
  title: string;
  description: string;
  topic?: string;
  status: string;
  priority: string;
  assignedTo: string;
  assignedUserId?: string;
  startDate?: string;
  completedAt?: string | null;
  dueDate?: string;
  entity: string;
  discipline: string;
  notes?: string[];
  acceptanceCriteria?: string;
};
export type NewTask = {
  title: string;
  description: string;
  priority: "Low" | "Medium" | "High" | "Critical";
  topic: string;
  initiativeId: string;
  entity: string;
  department: string;
  system: string;
  systemType: string;
  supplier: string;
  discipline: string;
  assignedTo: string;
  startDate: string;
  dueDate: string;
  estimatedHours: number | null;
};
const todayValue = () => new Date().toISOString().slice(0, 10);
const sprintDeadline = (startDate: string) => {
  if (!startDate) return "";
  const date = new Date(`${startDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 14);
  return date.toISOString().slice(0, 10);
};
export default function WorkCenterMyTasks({
  tasks,
  currentUserEmail,
  currentUserId,
  onOpen,
  onCreate,
  masterData,
  users,
  initiatives = [],
  statusColumns,
}: {
  tasks: Task[];
  currentUserEmail: string;
  currentUserId: string;
  onOpen: (id: string) => void;
  onCreate: (task: NewTask) => Promise<boolean>;
  masterData: WorkCenterMasterData;
  users: WorkCenterUser[];
  initiatives?: Initiative[];
  statusColumns: { key: string; label: string }[];
}) {
  const [showForm, setShowForm] = useState(false);
  const [displayMode, setDisplayMode] = useState<"list" | "board">("list");
  const [topicFilter, setTopicFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("current");
  const [newTask, setNewTask] = useState<NewTask>({
    title: "",
    description: "",
    priority: "Medium",
    topic: "",
    initiativeId: "",
    entity: "",
    department: "",
    system: "",
    systemType: "",
    supplier: "",
    discipline: "Applications",
    assignedTo: "",
    startDate: todayValue(),
    dueDate: sprintDeadline(todayValue()),
    estimatedHours: null,
  });
  useEffect(() => {
    if (currentUserEmail) setAssigneeFilter("current");
  }, [currentUserEmail]);
  const assignees = Array.from(
    new Set(
      [
        ...users.map((user) => user.email),
        ...tasks.map((task) => task.assignedTo),
      ].filter((value) => value && value !== "Unassigned"),
    ),
  ).sort((left, right) => left.localeCompare(right));
  const selectedAssignee = assigneeFilter === "all" ? "" : assigneeFilter;
  const visibleTasks = tasks
    .filter((task) => topicFilter === "all" || task.topic === topicFilter)
    .filter((task) => !dateFilter || task.dueDate === dateFilter)
    .filter(
      (task) =>
        !selectedAssignee ||
        (selectedAssignee === "current"
          ? Boolean(currentUserId) && (task.assignedUserId
            ? task.assignedUserId.toLowerCase() === currentUserId.toLowerCase()
            : Boolean(currentUserEmail) && task.assignedTo.trim().toLowerCase() === currentUserEmail.trim().toLowerCase())
          : task.assignedTo.trim().toLowerCase() === selectedAssignee.trim().toLowerCase()),
    )
    .sort((a, b) =>
      (a.dueDate || "9999-12-31").localeCompare(b.dueDate || "9999-12-31"),
    );
  const open = visibleTasks.filter((task) => task.status !== "done");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = open.filter(
    (task) => task.dueDate && new Date(task.dueDate) < today,
  ).length;
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!newTask.title.trim() || !newTask.description.trim() || !newTask.topic)
      return;
    const saved = await onCreate({
      ...newTask,
      title: newTask.title.trim(),
      description: newTask.description.trim(),
    });
    if (!saved) return;
    setNewTask({
      title: "",
      description: "",
      priority: "Medium",
      topic: "",
      initiativeId: "",
      entity: "",
      department: "",
      system: "",
      systemType: "",
      supplier: "",
      discipline: "Applications",
      assignedTo: "",
      startDate: todayValue(),
      dueDate: sprintDeadline(todayValue()),
      estimatedHours: null,
    });
    setShowForm(false);
  }
  const inputClass =
    "mt-1 w-full rounded-lg border border-slate-300 bg-white p-2.5 text-sm dark:border-slate-700 dark:bg-slate-950";
  return (
    <div className="h-full overflow-y-auto p-5 md:p-7">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[.18em] text-orange-500">
            Persoonlijk overzicht
          </p>
          <h2 className="mt-2 text-2xl font-black">Mijn openstaande taken</h2>
          <p className="mt-2 text-sm text-slate-500">
            Gesorteerd op deadline, van oud naar nieuw.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FoundryButton type="button" variant={displayMode === "list" ? "primary" : "secondary"} aria-pressed={displayMode === "list"} onClick={() => setDisplayMode("list")}>Lijst</FoundryButton>
          <FoundryButton type="button" variant={displayMode === "board" ? "primary" : "secondary"} aria-pressed={displayMode === "board"} onClick={() => setDisplayMode("board")}>Bord</FoundryButton>
          <FoundryBadge variant={overdue ? "red" : "green"}>
            {overdue} verlopen
          </FoundryBadge>
          <FoundryBadge>{open.length} openstaand</FoundryBadge>
          <FoundryButton
            type="button"
            variant="primary"
            onClick={() => setShowForm((value) => !value)}
          >
            {showForm ? "Annuleren" : "Taak toevoegen"}
          </FoundryButton>
        </div>
      </header>
      <FoundryCard className="mt-6">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-xs font-bold">
            Onderwerp
            <select value={topicFilter} onChange={(event) => setTopicFilter(event.target.value)} className={inputClass}>
              <option value="all">Alle onderwerpen</option>
              {masterData.topics.map((item) => <option key={item.code} value={item.name}>{item.name}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold">
            Datum
            <input type="date" value={dateFilter} onChange={(event) => setDateFilter(event.target.value)} className={inputClass} />
          </label>
          <label className="text-xs font-bold">
            Assignee
            <select value={assigneeFilter} onChange={(event) => setAssigneeFilter(event.target.value)} className={inputClass}>
              <option value="current">Mijn taken ({currentUserEmail || "ingelogde gebruiker"})</option>
              <option value="all">Alle assignees</option>
              {assignees.map((email) => <option key={email} value={email}>{email}</option>)}
            </select>
          </label>
        </div>
        <div className="mt-3 flex justify-end">
          <button type="button" className="text-xs font-bold text-orange-600 underline" onClick={() => { setTopicFilter("all"); setDateFilter(""); setAssigneeFilter("current"); }}>
            Filters wissen
          </button>
        </div>
      </FoundryCard>
      {showForm && (
        <FoundryCard className="mt-6">
          <form onSubmit={submit}>
            <h3 className="text-lg font-black">Nieuwe taak</h3>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="text-sm font-semibold md:col-span-2">
                Titel
                <input
                  required
                  maxLength={200}
                  value={newTask.title}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                  className={inputClass}
                />
              </label>
              <label className="text-sm font-semibold md:col-span-2">
                Omschrijving
                <textarea
                  required
                  maxLength={12000}
                  rows={3}
                  value={newTask.description}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  className={inputClass}
                />
              </label>
              <label className="text-sm font-semibold">
                Onderwerp *
                <select
                  required
                  value={newTask.topic}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      topic: event.target.value,
                      initiativeId: "",
                    }))
                  }
                  className={inputClass}
                >
                  <option value="">Kies een onderwerp</option>
                  {masterData.topics.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Initiatief (optioneel)
                <select value={newTask.initiativeId} onChange={(event) => setNewTask((current) => ({ ...current, initiativeId: event.target.value }))} className={inputClass}>
                  <option value="">Nog niet gekoppeld</option>
                  {initiatives.filter((item) => item.topic === masterData.topics.find((topic) => topic.code === newTask.topic)?.name).map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Prioriteit
                <select
                  value={newTask.priority}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      priority: event.target.value as NewTask["priority"],
                    }))
                  }
                  className={inputClass}
                >
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </label>
              <label className="text-sm font-semibold">
                Entiteit
                <select
                  value={newTask.entity}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      entity: event.target.value,
                    }))
                  }
                  className={inputClass}
                >
                  <option value="">Nog te bepalen</option>
                  {masterData.entities.map((item) => (
                    <option key={item.code} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Afdeling
                <select
                  value={newTask.department}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      department: event.target.value,
                    }))
                  }
                  className={inputClass}
                >
                  <option value="">Nog te bepalen</option>
                  {masterData.departments.map((item) => (
                    <option key={item.code} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Systeem
                <select
                  value={newTask.system}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      system: event.target.value,
                    }))
                  }
                  className={inputClass}
                >
                  <option value="">Nog te bepalen</option>
                  {masterData.systems.map((item) => (
                    <option key={item.code} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Systeemtype
                <select
                  value={newTask.systemType}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      systemType: event.target.value,
                    }))
                  }
                  className={inputClass}
                >
                  <option value="">Nog te bepalen</option>
                  {masterData.systemTypes.map((item) => (
                    <option key={item.code} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Leverancier
                <select
                  value={newTask.supplier}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      supplier: event.target.value,
                    }))
                  }
                  className={inputClass}
                >
                  <option value="">Nog te bepalen</option>
                  {masterData.suppliers.map((item) => (
                    <option key={item.code} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Discipline
                <select
                  value={newTask.discipline}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      discipline: event.target.value,
                    }))
                  }
                  className={inputClass}
                >
                  <option>Applications</option>
                  <option>Data & AI</option>
                  <option>Integrations</option>
                  <option>Infrastructure</option>
                  <option>Security</option>
                </select>
              </label>
              <label className="text-sm font-semibold">
                Toewijzen aan
                <select
                  value={newTask.assignedTo}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      assignedTo: event.target.value,
                    }))
                  }
                  className={inputClass}
                >
                  <option value="">Niet toegewezen</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.email}>
                      {user.displayName} ({user.email})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Startdatum *
                <input
                  required
                  type="date"
                  value={newTask.startDate}
                  onChange={(event) => setNewTask((current) => ({ ...current, startDate: event.target.value, dueDate: sprintDeadline(event.target.value) }))}
                  className={inputClass}
                />
                <span className="mt-1 block text-xs font-normal text-slate-500">De standaardperiode is 14 dagen.</span>
              </label>
              <label className="text-sm font-semibold">
                Deadline
                <input
                  type="date"
                  min={newTask.startDate || undefined}
                  value={newTask.dueDate}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      dueDate: event.target.value,
                    }))
                  }
                  className={inputClass}
                />
              </label>
              <label className="text-sm font-semibold">
                Inschatting (uren)
                <input
                  type="number"
                  min={0}
                  max={10000}
                  step="0.5"
                  value={newTask.estimatedHours ?? ""}
                  onChange={(event) =>
                    setNewTask((current) => ({
                      ...current,
                      estimatedHours:
                        event.target.value === ""
                          ? null
                          : Number(event.target.value),
                    }))
                  }
                  className={inputClass}
                />
              </label>
            </div>
            <FoundryButton type="submit" variant="primary" className="mt-5">
              Taak opslaan
            </FoundryButton>
          </form>
        </FoundryCard>
      )}
      {displayMode === "board" ? <div className="mt-6"><WorkCenterTaskBoard embedded tasks={visibleTasks} columns={statusColumns} renderTask={(task, accent) => <button key={task.id} type="button" onClick={() => onOpen(task.id)} className="w-full border-l-4 bg-white p-3 text-left text-sm dark:bg-slate-900" style={{ borderColor: accent }}><span className="block font-semibold">{task.title}</span><span className="mt-1 block text-xs text-slate-500">{task.topic || "Geen onderwerp"}</span></button>} /></div> : <div className="mt-6 space-y-3">
        {open.map((task) => {
          const isOverdue = task.dueDate && new Date(task.dueDate) < today;
          return (
            <button
              key={task.id}
              type="button"
              onClick={() => onOpen(task.id)}
              className="block w-full text-left"
            >
              <FoundryCard
                className={`border-l-4 transition hover:border-orange-400 ${isOverdue ? "border-l-red-500" : "border-l-slate-300 dark:border-l-slate-600"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <FoundryBadge
                        variant={
                          isOverdue
                            ? "red"
                            : task.priority === "Critical" ||
                                task.priority === "High"
                              ? "orange"
                              : "blue"
                        }
                      >
                        {isOverdue ? "Verlopen" : task.priority}
                      </FoundryBadge>
                      <FoundryBadge>{task.entity}</FoundryBadge>
                      <FoundryBadge>{task.discipline}</FoundryBadge>
                    </div>
                    <h3 className="mt-3 font-black">{task.title}</h3>
                    <p className="mt-2 text-sm text-slate-500">
                      {task.description}
                    </p>
                  </div>
                  <p
                    className={`shrink-0 text-sm font-black ${isOverdue ? "text-red-600" : "text-slate-600 dark:text-slate-300"}`}
                  >
                    {task.dueDate
                      ? new Intl.DateTimeFormat("nl-NL", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        }).format(new Date(task.dueDate))
                      : "Geen deadline"}
                  </p>
                </div>
              </FoundryCard>
            </button>
          );
        })}
        {!open.length && (
          <p className="rounded-xl bg-slate-100 p-5 text-sm text-slate-500 dark:bg-slate-900">
            {assigneeFilter === "current" && !currentUserId
              ? "Je Entra-gebruikers-ID kon niet worden vastgesteld. Kies Alle gebruikers of laad de pagina opnieuw."
              : "Geen openstaande taken voor deze filters."}
          </p>
        )}
      </div>}
    </div>
  );
}
